/*****************************************************
 * Packages
 ******************************************************/
import React, {
    useRef,
    useState,
    useEffect,
    useContext,
    forwardRef,
    useCallback,
    useImperativeHandle,
} from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { DragSource, DropTarget, DragPreviewImage } from 'react-dnd';

/*****************************************************
 * Locals
 ******************************************************/
import Borders from './other-components/Borders/Borders';
import previewComponents from './preview-components';
import styleGenerator from '@getanwar/style-generator';
import { ThemeContext } from '../ThemeContext';
import ElementControl from './ElementControl';
import * as dragIcons from '../util/dragIcons';
import isObject from '../util/isObject';
import { prefix } from '../config';
import idx from '../util/idx';
import getHideClass from '../util/getHideClass';

const DragHighlight = styled.div`
    opacity: 0.7;
    display: block;
    border-radius: 3px;
    height: ${({ itemType }) => (itemType === 'section' ? '40px' : '5px')};
    width: ${({ itemType }) => (itemType === 'column' ? '10px' : '100%')};
    background: ${({ theme, itemType }) =>
        theme.elementControls[itemType]
            ? theme.elementControls[itemType].bg
            : theme.elementControls.default.bg};
`;

function getCanDropBorder({ itemType, theme, canDrop, isDragging }) {
    if (!canDrop || itemType === 'column') return null;
    const elColor = theme.elementControls[itemType];
    let color = elColor ? elColor.bg : theme.elementControls.default.bg;
    return css`
        border-top: 1px dashed ${color};

        &:last-of-type {
            border-bottom: 1px dashed ${color};
        }
    `;
}

const ItemWrapper = styled.div`
    position: relative;
    opacity: ${({ isDragging, itemType }) =>
        isDragging ? (itemType === 'column' ? 0.5 : 0) : 1};
    height: ${({ isDragging, itemType }) =>
        isDragging ? (itemType === 'column' ? '' : 0) : ''};
    ${getCanDropBorder};
`;

/**
 * Generates and inject styles above of the element
 * @param {object} element
 * @returns {JSX} style
 */
const getStyles = (element) => {
    const styles = styleGenerator(element);
    return styles ? <style type="text/css">{styles}</style> : null;
};

// generate component className
const getElementClass = (item) => {
    let className = `${prefix}-${item.type}-${item.id}`;
    if (isObject(item.attr)) {
        Object.entries(item.attr).forEach(([key, value]) => {
            if (key.includes('__class__') || key === 'class') {
                className += ' ' + value;
            }
        });
    }
    return className.trim();
};

const DnDElement = forwardRef((props, ref) => {
    const {
        item,
        parentType,
        connectDropTarget,
        connectDragSource,
        connectDragPreview,
        isDragging,
        canDrop,
        address,
        onSaveSettings,
        children,
    } = props;
    const elementRef = useRef(null);
    const [canDropHightlight, setCanDropHightlight] = useState(false);
    const settingsWindowVisible = useSelector(
        (state) => state.config.settingsWindowVisible
    );

    useEffect(() => {
        if (canDrop) {
            setTimeout(() => {
                setCanDropHightlight(true);
            }, 0);
        } else {
            setCanDropHightlight(false);
        }
    }, [canDrop]);

    const [borderType, setBorderType] = useState('');
    const [isControlFrozen, setControlFrozen] = useState(false);
    const [isControlVisible, setControlVisibility] = useState(false);
    const [isTextEditorActive, setTextEditorActive] = useState(false);
    const { elementControls } = useContext(ThemeContext);
    const color = elementControls[item.type] || elementControls.default;
    const className = getElementClass(item);
    const Component = previewComponents[item.component_path];

    // Column Size. e.g: 1/2
    const getColSizeClasses = useCallback(() => {
        const lg = idx(item, (props) => props.attr.__class__columnSize);
        const md = idx(
            item,
            (props) => props.media.tablet.attr.__class__columnSize
        );
        const xs = idx(
            item,
            (props) => props.media.mobile.attr.__class__columnSize
        );
        let classes = '';
        if (lg) {
            classes += ` col-lg-${lg}`;
        }
        if (md) {
            classes += ` col-md-${md}`;
        }
        if (xs) {
            classes += ` col-xs-${xs}`;
        }
        return classes;
    }, [item]);

    const isRow = item.type === 'row';
    const isButton = item.type === 'button';
    let isInline = false;
    let container = isRow ? 'container' : '';
    if (isRow && item.wrapper && item.wrapper.width === '100%') {
        container = 'container-fluid';
    }
    if (isButton && item.settings) {
        isInline = item.settings.inlineButton;
    }

    const hiddenClass = getHideClass(item?.settings?.hideOn);

    // Element wrapper class
    const wrapperClass = `${getColSizeClasses().trim()} ${container} element-wrapper ${prefix}-${
        item.type
    }-${item.id}-wrapper ${isInline ? 'el-ib' : ''} ${hiddenClass}`;

    connectDragSource(elementRef);
    connectDropTarget(elementRef);
    useImperativeHandle(ref, () => ({
        getNode: () => elementRef.current,
    }));

    const freezeControlVisibility = React.useCallback(
        (boolean) => setControlFrozen(boolean),
        []
    );

    const triggerControlVisibility = useCallback((value) => {
        setTextEditorActive(!value);
    }, []);

    const handleMouseEnter = useCallback(() => {
        !isTextEditorActive && setControlVisibility(true);
    }, [isTextEditorActive]);
    const handleMouseLeave = useCallback(() => {
        !isControlFrozen && !isTextEditorActive && setControlVisibility(false);
    }, [isControlFrozen, isTextEditorActive]);

    if (!Component) {
        // TODO: Bug report to sentry
        return null;
    }
    return (
        <>
            <DragPreviewImage
                src={`/assets/images/${item.type}.png`}
                connect={connectDragPreview}
            />

            <ItemWrapper
                ref={elementRef}
                itemType={item.type}
                isDragging={isDragging}
                canDrop={canDropHightlight}
                className={wrapperClass.trim()}
                onMouseLeave={handleMouseLeave}
                onMouseEnter={handleMouseEnter}
            >
                {getStyles(item)}
                {!settingsWindowVisible && isControlVisible && (
                    <ElementControl
                        className={isTextEditorActive ? 'hidden' : null}
                        freezeControlVisibility={freezeControlVisibility}
                        onMouseEnter={(e) => setBorderType(item.type)}
                        setControlVisibility={setControlVisibility}
                        connectDragSource={connectDragSource}
                        onMouseLeave={() => setBorderType('')}
                        setBorderType={setBorderType}
                        parentType={parentType}
                        address={address}
                        color={color}
                        item={item}
                    />
                )}

                <Component
                    // updateSpacing={actionProps.updateSpacing}
                    triggerControlVisibility={triggerControlVisibility}
                    onSaveSettings={onSaveSettings}
                    className={className}
                    address={address}
                    item={item}
                >
                    {children}
                </Component>
                {borderType === item.type && <Borders color={color} />}
            </ItemWrapper>

            {isDragging && item.type !== 'column' && (
                <DragHighlight itemType={item.type} />
            )}
        </>
    );
});

DnDElement.displayName = 'DnDElement';

const getNumericAddress = (address) => Number(address.split('.').join(''));

const hover = (props, monitor, component) => {
    if (!component) {
        return null;
    }
    // node = HTML Div element from imperative API
    const node = component.getNode();

    if (!node) {
        return null;
    }
    const sourceItem = monitor.getItem();
    // Don't replace items with themselves
    if (sourceItem.address === props.address) {
        return;
    }

    const source = {
        index: sourceItem.index,
        address: sourceItem.address,
        parent: sourceItem.parentAddress,
        numericAddress: getNumericAddress(sourceItem.address),
    };

    const destination = {
        index: props.index,
        address: props.address,
        parent: props.parentAddress,
        numericAddress: getNumericAddress(props.address),
    };

    // Determine rectangle on screen
    const hoverBoundingRect = node.getBoundingClientRect();
    // Get vertical middle
    const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
    // Get horizontal middle
    const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
    // Determine mouse position
    const clientOffset = monitor.getClientOffset();
    // Get pixels to the top
    const hoverClientY = clientOffset.y - hoverBoundingRect.top;

    // are we dragging right or left?
    const dragRight = source.numericAddress === destination.numericAddress - 1;
    const dragLeft = source.numericAddress === destination.numericAddress + 1;

    // are we dragging up or down?
    const dragUp = sourceItem.dragBoundingRect.top > hoverBoundingRect.top;
    const dragDown =
        sourceItem.dragBoundingRect.bottom < hoverBoundingRect.bottom;
    // Get pixels to the left
    const hoverClientX = clientOffset.x - hoverBoundingRect.left;
    const upwards =
        source.numericAddress > destination.numericAddress &&
        hoverClientY > hoverMiddleY;
    const downwards =
        source.numericAddress < destination.numericAddress &&
        hoverClientY < hoverMiddleY;
    const leftwards =
        source.numericAddress > destination.numericAddress &&
        hoverClientX > hoverMiddleX;
    const rightwards =
        source.numericAddress < destination.numericAddress &&
        hoverClientX < hoverMiddleX;
    // Only perform the move when the mouse has crossed half of the items height
    // When dragging downwards, only move when the cursor is below 50%
    // When dragging upwards, only move when the cursor is above 50%
    // Dragging downwards
    if (sourceItem.type === 'column') {
        if (dragLeft && leftwards) return;
        if (dragRight && rightwards) return;
        if (dragUp && upwards) return;
        if (dragDown && downwards) return;
    } else {
        // Moving down: exit if we're in upper half
        if (downwards) {
            return;
        }

        // Moving up: exit if we're in lower half
        if (upwards) {
            return;
        }
    }

    props.onDragItem({ source, destination });
    monitor.getItem().index = destination.index;
    monitor.getItem().address = destination.address;
    monitor.getItem().parentAddress = destination.parent;
};

const drop = (props, monitor, component) => {
    const sourceItem = monitor.getItem();
    // Don't replace items with themselves
    if (sourceItem.address === props.address) {
        return;
    }

    const source = {
        index: sourceItem.index,
        address: sourceItem.address,
        parent: sourceItem.parentAddress,
        numericAddress: getNumericAddress(sourceItem.address),
    };

    const destination = {
        index: props.index,
        address: props.address,
        parent: props.parentAddress,
        numericAddress: getNumericAddress(props.address),
    };
    props.onDragItem({ source, destination });
    monitor.getItem().index = destination.index;
    monitor.getItem().address = destination.address;
    monitor.getItem().parentAddress = destination.parent;
};

export default DropTarget(
    (props) => props.parentType,
    {
        hover,
        // drop,
    },
    (connect, monitor) => {
        return {
            canDrop: monitor.canDrop(),
            connectDropTarget: connect.dropTarget(),
        };
    }
)(
    DragSource(
        (props) => props.parentType,
        {
            beginDrag: (props, monitor, component) => {
                const node = component.getNode();
                const dragBoundingRect = node.getBoundingClientRect();

                return {
                    type: props.type,
                    dragBoundingRect,
                    index: props.index,
                    address: props.address,
                    parentAddress: props.parentAddress,
                };
            },
            isDragging(props, monitor) {
                return props.address === monitor.getItem().address;
            },
        },
        (connect, monitor) => {
            const item = monitor.getItem();
            return {
                connectDragSource: connect.dragSource(),
                connectDragPreview: connect.dragPreview(),
                isDragging: monitor.isDragging(),
                itemAddress: item?.address,
            };
        }
    )(DnDElement)
);
